import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    OnInit,
    Output,
    Renderer2,
    ViewEncapsulation
} from '@angular/core';
import { DeviceIdService, OnboardingService } from '@ridango/core/lib/services';
import { ApplicationFacade, CustomerFacade } from '@ridango/components';
import { AuthService } from '@ridango/core';
import { Capacitor } from '@capacitor/core';
import { OnboardingType } from '@ridango/widgets';
import { Navigation, Pagination, Keyboard, SwiperOptions, Swiper } from 'swiper';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { Observable, timer } from 'rxjs';
import Env from '@ridango/core/env';
import { StatusBarService } from '@ridango/core/lib/services/status-bar.service';
import { ScreenOrientationService } from '@ridango/core/lib/services/screenOrientation.service';

Swiper.use([Pagination, Keyboard, Navigation]);

@Component({
    selector: 'gl-onboarding-page',
    templateUrl: './onboarding-page.component.html',
    styleUrls: ['./onboarding-page.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class OnboardingPageComponent implements OnInit, AfterViewInit {
    @Output() highContrastToggle: EventEmitter<void> = new EventEmitter();
    public assetsPath$ = this.applicationFacade.assetsPath$;

    public onboardingStatus: string;
    public isSlideEnd = false;
    public onboardingType = OnboardingType;
    public swiper: Swiper | undefined;
    private swiperNextBtn?: HTMLElement;
    public termsAgreed = false;
    public isDisableNavigation = false;
    public isShowTermsAndConditions = this.env.ONBOARDING.SUB_PAGES.includes('TERMS');
    private isAnonymousUser = false;
    private readonly shareLinkLocal = 'share-link';
    currentSlide = 0;
    onlyPortraitMode$: Observable<boolean> = this.screenOrientationService.onlyPortraitMode$;

    constructor(
        @Inject('ENV') public env: Env,
        private applicationFacade: ApplicationFacade,
        private ridangoAuth: AuthService,
        private onboardingService: OnboardingService,
        private deviceIdService: DeviceIdService,
        private router: Router,
        private translate: TranslateService,
        private renderer: Renderer2,
        private statusBarColor: StatusBarService,
        private customerFacade: CustomerFacade,
        private ref: ChangeDetectorRef,
        private screenOrientationService: ScreenOrientationService
    ) {}

    public noticesSliderConf: SwiperOptions = {
        slidesPerView: 1,
        speed: 500,
        navigation: {
            enabled: true
        },
        keyboard: {
            enabled: true
        },
        pagination: {
            clickable: !this.env.ONBOARDING.DISABLE_CLICKABLE_PAGINATION
        }
    };

    ngOnInit(): void {
        this.onboardingStatus = this.onboardingService.onboardingStatus;

        if (this.onboardingStatus === OnboardingType.COMPLETED) {
            this.router.navigate([this.translate.currentLang]);
        } else if (
            this.onboardingStatus !== OnboardingType.STARTED &&
            this.onboardingStatus !== OnboardingType.ONBOARD_FLOW
        ) {
            this.setStatusAfterSlides();
        }
    }

    ngAfterViewInit() {
        this.addAriaLabels();
    }

    public get isFirstSlide(): boolean {
        return this.swiper?.activeIndex === 0;
    }

    public updateOnBoardingStatus(status: OnboardingType): void {
        this.onboardingService.onboardingStatus = status;
        this.onboardingStatus = status;
        this.ref.detectChanges();

        if (this.onboardingStatus === OnboardingType.ONBOARD_FLOW) {
            timer(10).subscribe(() => {
                this.addAriaLabels();
                this.registerUser();
            });

            if (this.env.STATUS_BAR?.COLOR_ONBOARD_FLOW) {
                this.statusBarColor.setStatusColor(this.env.STATUS_BAR.COLOR_ONBOARD_FLOW);
            }
        }
    }

    public toggleContrast(): void {
        this.applicationFacade.switchHighContrastMode();
        this.applicationFacade.highContrast$.pipe(take(1)).subscribe((isContrast) => {
            if (isContrast) {
                this.renderer.addClass(document.body, 'high-contrast');
                this.renderer.addClass(document.documentElement, 'dark');
                localStorage.setItem('theme', 'dark');
            } else {
                this.renderer.removeClass(document.body, 'high-contrast');
                this.renderer.removeClass(document.documentElement, 'dark');
                localStorage.removeItem('theme');
            }
        });
    }

    public clickToLogin(event): void {
        if (this.isDisableNavigation) return;
        const classList = event.target?.classList;
        if (
            this.isSlideEnd &&
            classList?.contains('swiper-button-next') &&
            classList?.contains('swiper-button-disabled')
        ) {
            this.setStatusAfterSlides();
        }
    }

    public touchToLogin(event): void {
        if (this.isDisableNavigation) return;

        if (
            event[0].isEnd &&
            event[0].swipeDirection === 'next' &&
            (!this.isShowTermsAndConditions || this.termsAgreed)
        ) {
            this.setStatusAfterSlides();
        }
    }

    public onSwiperInit(swiper: Swiper): void {
        this.swiper = swiper;
        if (this.env.IS_NEW_AFC && swiper.isBeginning) {
            const swiperBackButton = this.swiper.el.querySelector('.swiper-button-prev');
            this.renderer.removeClass(swiperBackButton, 'swiper-button-disabled');
            swiperBackButton.addEventListener('click', this.handleBackButtonClick);
        }
        this.ref.detectChanges();
        const nextEl = swiper.navigation?.nextEl;
        this.swiperNextBtn = nextEl instanceof HTMLElement ? nextEl : undefined;
    }

    private handleBackButtonClick = (event: MouseEvent) => {
        this.onboardingStatus = this.onboardingType.STARTED;
        this.termsAgreed = false;
        event.target.removeEventListener('click', this.handleBackButtonClick);
    };

    public onSlideChange(): void {
        this.currentSlide = this.swiper.realIndex;
        if (this.env.IS_NEW_AFC) {
            const swiperBackButton = this.swiper.el.querySelector('.swiper-button-prev');

            if (this.swiper.isBeginning) {
                this.renderer.removeClass(swiperBackButton, 'swiper-button-disabled');
                swiperBackButton.addEventListener('click', this.handleBackButtonClick);
            } else {
                swiperBackButton.removeEventListener('click', this.handleBackButtonClick);
            }
        }

        if (this.swiper && this.isShowTermsAndConditions) {
            this.handleTermsAgree();
        }
        this.ref.detectChanges();
    }
    public setTermsAgreed(value: boolean): void {
        this.termsAgreed = value;
        this.handleTermsAgree();
    }

    private handleTermsAgree(): void {
        const isLastSlide = this.swiper.isEnd;

        if (isLastSlide) {
            this.swiper.allowSlidePrev = this.env.IS_NEW_AFC;
            this.swiper.allowSlideNext = this.termsAgreed;
            this.swiper.attachEvents();
        } else {
            this.swiper.allowSlideNext = true;
        }
        this.isDisableNavigation = !this.env.IS_NEW_AFC && isLastSlide && !this.termsAgreed;

        if (isLastSlide && !this.termsAgreed) {
            this.removeSwiperNextBtn();
        } else {
            this.restoreSwiperNextBtn();
        }

        this.ref.detectChanges();
    }

    private removeSwiperNextBtn(): void {
        if (this.swiper && this.swiperNextBtn?.parentNode) {
            this.swiperNextBtn.parentNode.removeChild(this.swiperNextBtn);
        }
    }

    private restoreSwiperNextBtn(): void {
        if (!this.swiper || !this.swiperNextBtn || this.swiperNextBtn.isConnected) return;
        this.swiper.el.appendChild(this.swiperNextBtn);
        this.swiper.navigation.update();
    }

    private setStatusAfterSlides(): void {
        const shareJourneyLink = localStorage.getItem(this.shareLinkLocal);

        this.applicationFacade.setUserAgreedTermsVersionOnOnboarding(this.applicationFacade.getTerms()?.version);

        if (shareJourneyLink && this.env.JOURNEY_PLANNER?.SHOW_ROUTE_SHARE) {
            this.updateOnBoardingStatus(this.onboardingType.COMPLETED);
            this.router.navigateByUrl(shareJourneyLink);
            localStorage.removeItem(this.shareLinkLocal);
            return;
        }

        if (this.ridangoAuth.getLoginStatus()) {
            this.updateOnBoardingStatus(this.onboardingType.COMPLETED);
        } else {
            this.updateOnBoardingStatus(this.onboardingType.LOGIN_FLOW);
        }
    }

    private registerUser() {
        if (Capacitor.isNativePlatform() && !this.isAnonymousUser) {
            this.isAnonymousUser = true;

            this.deviceIdService
                .isDeviceUserExist()
                .pipe(take(1))
                .subscribe(async (isUserExist) => {
                    await this.deviceIdService.setLocalUserExist(isUserExist).toPromise();

                    this.deviceIdService
                        .refreshAccessDeviceToken()
                        .pipe(take(1))
                        .subscribe(() => {
                            if (this.env.IS_NEW_AFC) {
                                return;
                            }
                            this.customerFacade.sendCustomerLang(this.translate.currentLang);
                        });
                });
        }

        if (!Capacitor.isNativePlatform()) {
            this.deviceIdService
                .refreshAccessDeviceToken()
                .pipe(take(1))
                .subscribe(() => {
                    this.customerFacade.sendCustomerLang(this.translate.currentLang);
                });
        }
    }

    private addAriaLabels() {
        const nextButton = document.getElementsByClassName('swiper-button-next')[0];
        const backButton = document.getElementsByClassName('swiper-button-prev')[0];

        nextButton?.setAttribute('aria-label', this.translate.instant('SWITCH_TO_NEXT'));
        backButton?.setAttribute('aria-label', this.translate.instant('SWITCH_TO_PREVIOUS'));
        nextButton?.setAttribute('role', 'button');
        backButton?.setAttribute('role', 'button');
    }

    trackByIndex(index: number): number {
        return index;
    }
}
